import type { Expense } from '../../../types';
import type { TripMemberUser } from '../../../utils/tripMembers';

/** The totals a filtered ledger view needs. Same shape — and the same names —
 *  the server's `/expenses/summary` produces (`expenses.service.ts`), so the two
 *  arithmetics stay comparable. */
export interface LedgerSummary {
  total:      number;
  count:      number;
  byCategory: Record<string, number>;
  /** What each person LAID OUT. */
  byPayer:    Record<string, number>;
  /** What each person's share COMES TO, from the splits. Not the same question
   *  as `byPayer`, and the whole reason the per-person table has two columns:
   *  whoever pays the restaurant has not eaten everyone else's dinner. */
  byParticipant: Record<string, number>;
  byDay:      Record<string, number>;
}

/**
 * Totals over whatever slice of the ledger is on screen.
 *
 * The server endpoint always sums the whole trip, which is the one thing these
 * cards can no longer show once a filter is on. `listExpenses` returns the trip
 * unpaginated, so the client holds exactly the same rows and its arithmetic is
 * the same arithmetic.
 */
export function summarize(expenses: Expense[]): LedgerSummary {
  const byCategory:    Record<string, number> = {};
  const byPayer:       Record<string, number> = {};
  const byParticipant: Record<string, number> = {};
  const byDay:         Record<string, number> = {};
  let total = 0;

  for (const e of expenses) {
    const amount = Number(e.amount);
    const day = e.date.slice(0, 10);
    total += amount;
    byCategory[e.category] = (byCategory[e.category] ?? 0) + amount;
    byPayer[e.payerId]     = (byPayer[e.payerId] ?? 0) + amount;
    byDay[day]             = (byDay[day] ?? 0) + amount;
    for (const s of e.splits) {
      byParticipant[s.userId] = (byParticipant[s.userId] ?? 0) + Number(s.amount);
    }
  }

  return { total, count: expenses.length, byCategory, byPayer, byParticipant, byDay };
}

/** Descending count per key, the order the filter pills are shown in. */
export function countBy<T>(items: T[], key: (item: T) => string): [string, number][] {
  const counts: Record<string, number> = {};
  for (const item of items) {
    const k = key(item);
    counts[k] = (counts[k] ?? 0) + 1;
  }
  return Object.entries(counts).sort(([, a], [, b]) => b - a);
}

/**
 * Everyone the ledger has to account for: the trip's members, plus anybody who
 * paid something and is no longer one.
 *
 * Removing a participant deletes their row, not their expenses — those stay in
 * the total. A per-person table built from the member list alone would drop
 * them and quietly stop adding up.
 */
export function ledgerPeople(members: TripMemberUser[], expenses: Expense[]): TripMemberUser[] {
  const seen = new Set(members.map(m => m.id));
  const extra: TripMemberUser[] = [];
  for (const e of expenses) {
    if (!e.payer || seen.has(e.payerId)) continue;
    seen.add(e.payerId);
    extra.push({ ...e.payer, avatarUrl: null });
  }
  return [...members, ...extra];
}
